import { FocusMonitor } from '@angular/cdk/a11y';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  Inject,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { MatStepper } from '@angular/material/stepper';

/**
 * Custom styled stepper component for RDS applications.
 * Wraps mat stepper and provides custom styles and controls.
 *
 * @author Will Davis <will.davis@mtna.us>
 */
@Component({
  selector: 'rds-c-stepper',
  templateUrl: './stepper.component.html',
  styleUrls: ['./stepper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class RdsStepperComponent {
  static ngAcceptInputType_nextButtonCompleteText: string | null | undefined;
  static ngAcceptInputType_nextButtonDisabled: BooleanInput;
  static ngAcceptInputType_nextButtonText: string | null | undefined;

  @ContentChild(MatStepper) matStepper?: MatStepper;

  /** The text for the next button when the last step is active, defaults to `COMPLETE`. Overrides the `nextButtonText`. */
  @Input()
  get nextButtonCompleteText(): string {
    return this._nextButtonCompleteText;
  }
  set nextButtonCompleteText(value: string) {
    this._nextButtonCompleteText = value || 'COMPLETE';
  }
  private _nextButtonCompleteText = 'COMPLETE';

  /** Whether the next button should be disabled, defaults to `false` */
  @Input()
  get nextButtonDisabled(): boolean {
    return this._nextButtonDisabled;
  }
  set nextButtonDisabled(disableNextButton: boolean) {
    disableNextButton = coerceBooleanProperty(disableNextButton);
    if (this._nextButtonDisabled !== disableNextButton) {
      this._nextButtonDisabled = disableNextButton;
    }
  }
  private _nextButtonDisabled = false;

  /** The text for the next button, defaults to `CONTINUE` */
  @Input()
  get nextButtonText(): string {
    return this._nextButtonText;
  }
  set nextButtonText(value: string) {
    this._nextButtonText = value || 'CONTINUE';
  }
  private _nextButtonText = 'CONTINUE';

  /** Notifies that the final `forward` button has been triggered */
  @Output() done = new EventEmitter();

  constructor(private cdr: ChangeDetectorRef, @Inject(DOCUMENT) private document: Document, private focusMonitor: FocusMonitor) {}

  handleContentChange() {
    this.cdr.markForCheck();
  }

  /** Selects and focuses the next step in list. */
  next() {
    if (this.matStepper) {
      if (this.matStepper.selectedIndex === this.matStepper.steps.length - 1) {
        this.done.emit();
      }
      this._restoreFocusToSelectedStep();
      this.matStepper.next();
    }
  }

  /** Selects and focuses the previous step in list. */
  previous() {
    if (this.matStepper) {
      this._restoreFocusToSelectedStep();
      this.matStepper.previous();
    }
  }

  _restoreFocusToSelectedStep() {
    if (this.matStepper) {
      // When stepper next()'s/previous()'s, it draws focus to the new step's header ONLY if the stepper contains focus
      // So we need to restore focus within the stepper before this, in order to make focus occur on the new step
      const selectedStepLabel = this.document.getElementById(this.matStepper._getStepLabelId(this.matStepper.selectedIndex));
      if (selectedStepLabel) {
        this.focusMonitor.focusVia(selectedStepLabel, 'keyboard');
      }
    }
  }
}
